import { Children, useState, useEffect, cloneElement } from 'react';
import { jsx } from '@emotion/react';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';
import '@indeed/ifl-css';

/**
 * @see https://experience.indeed.design/components/ids/live-region
 */
const LiveRegion = _ref => {
  let {
    children,
    type = "polite"
  } = _ref;
  const element = Children.only(children);
  const elementChildren = element.props.children;

  /**
   * Trigger screen reader announcement.
   *
   * Background:
   *
   * In general, live regions only operate on readable updates to the element's
   * children.
   *
   * Solution:
   *
   * By appending a visually-hidden, audible character (period) on render
   * and removing it after a set duration, screen readers will recognize
   * the change as an update and trigger an announcement. Setting `aria-atomic`
   * to `true` ensures that the screen reader will always present the live
   * region as a whole, even if only part of the region has changed (i.e.
   * removing the visually-hidden character).
   *
   */
  const [announce, setAnnounce] = useState(false);
  useEffect(() => {
    let timer;
    if (type) {
      timer = window.setTimeout(() => {
        setAnnounce(true);
      }, 500);
    }
    return () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [type]);
  const newChildren = Children.toArray([elementChildren, !announce && jsx(VisuallyHidden, null, ".")]);
  return /*#__PURE__*/cloneElement(element, {
    "aria-live": type,
    "aria-atomic": true
  }, newChildren);
};
const LIVE_REGION_TYPES = ["polite", "assertive"];
LiveRegion.displayName = "LiveRegion";

export { LIVE_REGION_TYPES, LiveRegion };
